import { useState, useContext, useMemo, useEffect, useRef } from 'react';

import { TwilioContext } from '../context-providers/TwilioContext';
import { getCallById } from '../api';
import { CallInfoBuilderClass } from '../utils/CallInfoBuilderClass';
import { CallWithDuration, CallDetailResponse, GetCustomerAPIDataType } from '../types';

import useFetch from './useFetch';
import useCallControls from './useCallControls';
import useCustomerData from './useCustomerData';
import useCallPolling from './useCallPolling';

type UseCallInfoBuilderOptions = {
  sid?: string;
  callId?: number;
  stopOnCallId?: boolean;
};

type UseCallInfoBuilderReturn = {
  callInfo: CallInfoBuilderClass;
  isLoading: boolean;
  error: string | null;
  foundCallId: number | null;
  activeCall: CallWithDuration | undefined;
  status: string | undefined;
  refetchCallDetail: () => void;
};

/**
 * Hook for building CallInfoAggregated from multiple data sources.
 * Supports progressive rendering - shows data as soon as ANY source is available.
 * Preserves data during transitions (e.g., when active call ends).
 *
 * Entry points:
 * - sid: Look up by Twilio SID (for /call/new page)
 * - callId: Look up by database call ID (for /call/:id page)
 *
 * Options:
 * - stopOnCallId: When true, stops enriching once callId is found (for redirect)
 */
const useCallInfoBuilder = (options: UseCallInfoBuilderOptions): UseCallInfoBuilderReturn => {
  const { sid, callId, stopOnCallId = false } = options;
  const { calls } = useContext(TwilioContext);

  // Track the found call ID for redirect support
  const [foundCallId, setFoundCallId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Track resolved SID from API response (for matching inbound calls on /call/:callId)
  const [resolvedSid, setResolvedSid] = useState<string | null>(null);

  // Keep track of previous callId to reset state when it changes
  const prevCallIdRef = useRef<number | undefined>(callId);

  // Cache refs for API responses (to prevent blink during refetch)
  const cachedCallDetailRef = useRef<CallDetailResponse | null>(null);
  const cachedCustomerDataRef = useRef<GetCustomerAPIDataType | null>(null);

  // Clear cache refs synchronously during render (before useMemo runs)
  // This prevents stale data from showing when navigating between call pages
  if (callId !== prevCallIdRef.current) {
    cachedCallDetailRef.current = null;
    cachedCustomerDataRef.current = null;
    prevCallIdRef.current = callId;
  }

  // Reset state when callId changes (must be in useEffect, can't set state during render)
  useEffect(() => {
    setError(null);
    setFoundCallId(null);
    setResolvedSid(null);
  }, [callId]);

  // Find matching active call from TwilioContext
  const activeCall = useMemo((): CallWithDuration | undefined => {
    const callsWithDuration = calls as CallWithDuration[];

    // By callId (database ID)
    if (callId) {
      const match = callsWithDuration.find(call => call.dbCallId === callId);
      if (match) return match;
    }

    // By SID (from prop or resolved from API response)
    const effectiveSid = sid || resolvedSid;
    if (effectiveSid) {
      const match = callsWithDuration.find(call => call.parameters?.CallSid === effectiveSid);
      if (match) return match;
    }

    return undefined;
  }, [calls, callId, sid, resolvedSid]);

  // Get call controls (status, duration, mute, hold)
  const { status, callDuration, isMuted, isHolded, getCustomerPhoneNumber } =
    useCallControls(activeCall);

  // Poll for call ID when we have SID but no callId yet
  const shouldPoll = !!sid && !callId && !foundCallId;
  const {
    foundCallId: polledCallId,
    isPolling,
    error: pollingError,
  } = useCallPolling(sid, shouldPoll);

  // Update foundCallId when polling succeeds
  useEffect(() => {
    if (polledCallId) {
      setFoundCallId(polledCallId);
      // Also set the dbCallId on the active call for matching on detail page
      if (activeCall && !activeCall.dbCallId) {
        activeCall.dbCallId = polledCallId;
      }
    }
  }, [polledCallId, activeCall]);

  // Determine which callId to use for API fetch
  const effectiveCallId = callId ?? foundCallId;

  // Fetch call detail from API
  const {
    data: fetchedCallDetailResponse,
    isLoading: isCallDetailLoading,
    error: callDetailError,
    refetch: refetchCallDetail,
  } = useFetch<CallDetailResponse | null>(
    () => {
      if (!effectiveCallId) return Promise.resolve(null);
      // If stopOnCallId and we found the ID, skip fetching full details
      if (stopOnCallId && foundCallId) return Promise.resolve(null);
      return getCallById(effectiveCallId);
    },
    [effectiveCallId, stopOnCallId, foundCallId],
  );

  // Verify fetched data matches the current request (prevents stale data on callId change)
  // useFetch clears data asynchronously in useEffect, so on first render after callId changes,
  // fetchedCallDetailResponse may still contain data from the previous callId
  const dataMatchesRequest = effectiveCallId ?
    fetchedCallDetailResponse?.id === effectiveCallId :
    fetchedCallDetailResponse === null;

  // Cache call detail response to prevent blink during refetch
  // Only cache if data matches the current request
  if (fetchedCallDetailResponse && dataMatchesRequest) {
    cachedCallDetailRef.current = fetchedCallDetailResponse;
  }
  const callDetailResponse = dataMatchesRequest ?
    (fetchedCallDetailResponse ?? cachedCallDetailRef.current) :
    null;

  // Set resolvedSid and dbCallId when API response contains the external_key (SID)
  // This enables matching active inbound calls on /call/:callId page
  useEffect(() => {
    if (!callDetailResponse?.external_key || !callDetailResponse?.id) return;

    // Set resolvedSid to trigger activeCall re-calculation
    if (callDetailResponse.external_key !== resolvedSid) {
      setResolvedSid(callDetailResponse.external_key);
    }

    // Also set dbCallId on the call object for future matching
    const match = (calls as CallWithDuration[]).find(
      c => c.parameters?.CallSid === callDetailResponse.external_key,
    );
    if (match && !match.dbCallId) {
      match.dbCallId = callDetailResponse.id;
    }
  }, [calls, callDetailResponse?.external_key, callDetailResponse?.id, resolvedSid]);

  // Track previous active state for refetch on transition
  const prevActiveRef = useRef(!!activeCall);

  // Auto-refetch when call transitions from active to inactive
  useEffect(() => {
    if (prevActiveRef.current && !activeCall && effectiveCallId) {
      // Call just became inactive, refetch to get updated status
      setTimeout(() => refetchCallDetail(), 1500);
    }
    prevActiveRef.current = !!activeCall;
  }, [activeCall, effectiveCallId, refetchCallDetail]);

  // Determine phone number for customer data fetch
  const phoneNumber = useMemo(() => {
    // From active call
    if (activeCall) {
      return getCustomerPhoneNumber();
    }
    // From API response
    if (callDetailResponse) {
      const direction = callDetailResponse.direction;
      return direction === 'inbound' ?
        callDetailResponse.from_phone_number :
        callDetailResponse.to_phone_number;
    }
    return undefined;
  }, [activeCall, callDetailResponse, getCustomerPhoneNumber]);

  // Fetch customer data
  const { customerData: fetchedCustomerData, isLoading: isCustomerLoading } =
    useCustomerData(phoneNumber);

  // Cache customer data to prevent blink when useCustomerData refetches
  // Only use customer data if we have a valid phoneNumber to match against
  // (prevents stale customer data when callDetailResponse is cleared)
  const shouldUseCustomerData = phoneNumber !== undefined;

  if (fetchedCustomerData && shouldUseCustomerData) {
    cachedCustomerDataRef.current = fetchedCustomerData;
  }
  const customerData = shouldUseCustomerData ?
    (fetchedCustomerData ?? cachedCustomerDataRef.current) :
    null;

  // Build call info progressively from all cached data sources using useMemo
  // This ensures we preserve data during transitions (e.g., when active call ends)
  const callInfo = useMemo(() => {
    let info = CallInfoBuilderClass.create();

    // 1. Start with call detail API response (primary source for historical data)
    if (callDetailResponse) {
      info = info.withCallDetailResponse(callDetailResponse);
    }

    // 2. Try to match and enrich with active call data
    // Uses withActiveCallMatch which preserves existing data if no match found
    if (calls.length > 0 && status) {
      info = info.withActiveCallMatch(calls as CallWithDuration[], {
        callId,
        sid,
        status,
        duration: callDuration,
        isMuted,
        isOnHold: isHolded,
      });
    }

    // 3. Enrich with customer data
    if (customerData) {
      info = info.withCustomerData(customerData);
    }

    // 4. Add found call ID if we have it (from polling) and not already set
    if (foundCallId && !info.id) {
      info = info.withCallId(foundCallId);
    }

    return info;
  }, [
    callDetailResponse,
    calls,
    callId,
    sid,
    status,
    callDuration,
    isMuted,
    isHolded,
    customerData,
    foundCallId,
  ]);

  // Set error from various sources
  useEffect(() => {
    if (pollingError) {
      setError(pollingError);
    } else if (callDetailError) {
      setError(callDetailError);
    } else {
      setError(null);
    }
  }, [pollingError, callDetailError]);

  // Calculate loading state
  const isLoading = isPolling || isCallDetailLoading || (isCustomerLoading && !callInfo.hasData());

  return {
    callInfo,
    isLoading,
    error,
    foundCallId,
    activeCall,
    status,
    refetchCallDetail,
  };
};

export default useCallInfoBuilder;
